import murmur from 'murmurhash';

const SHA256_HEX_REGEX = /^[a-f0-9]{64}$/;

function normalizeWhitespace(value: string): string {
    return value.trim();
}

/**
 * Generate a stable numeric ID from a string using MurmurHash
 * @param str - The string to hash
 * @param seed - Optional seed value (default: 0)
 * @returns A positive 32-bit integer hash
 */
export function stringToNumericId(str: string, seed = 0): number {
    return murmur.v3(str, seed) >>> 0; // Ensure positive 32-bit integer
}

/**
 * Check whether a value is a lowercase SHA-256 hex digest.
 * @param value - Value to validate
 * @returns True if the value matches a SHA-256 hex digest
 */
export function isSha256Hex(value: string): boolean {
    return SHA256_HEX_REGEX.test(value);
}

/**
 * Normalize an email address for hashing.
 * @param value - Raw email value
 * @returns Trimmed, lowercase email
 */
export function normalizeEmail(value: string): string {
    return normalizeWhitespace(value).toLowerCase();
}

/**
 * Normalize an external ID for hashing.
 * @param value - Raw external ID value
 * @returns Trimmed, lowercase external ID
 */
export function normalizeExternalId(value: string): string {
    return normalizeWhitespace(value).toLowerCase();
}

/**
 * Generate a lowercase SHA-256 hex digest for a string.
 * @param value - Input value to hash
 * @returns SHA-256 hex string
 */
export async function sha256Hex(value: string): Promise<string> {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Hash a normalized value only when needed.
 * @param value - Raw input value
 * @param normalizer - Normalizer function to apply before hashing
 * @returns Undefined for missing/blank input, normalized pre-hash if already SHA-256, or newly hashed value
 */
export async function hashIfNeeded(
    value: string | undefined,
    normalizer: (input: string) => string,
): Promise<string | undefined> {
    if (!value) {
        return undefined;
    }

    const normalized = normalizer(value);
    if (!normalized) {
        return undefined;
    }

    if (isSha256Hex(normalized)) {
        return normalized;
    }

    return sha256Hex(normalized);
}

/**
 * Generate a stable numeric ID from a string or return a fallback
 * Useful for MapLibre feature IDs which must be numbers
 * @param id - The string ID to hash
 * @param fallback - Fallback value if id is not provided
 * @returns A numeric ID suitable for MapLibre feature IDs
 */
export function getNumericId(id: string | undefined | null, fallback: number): number {
    if (!id) {
        return fallback;
    }
    return stringToNumericId(id);
}

/**
 * Generate a stable hex hash from a File or ArrayBuffer using MurmurHash
 * @param file - The File or ArrayBuffer to hash
 * @param seed - Optional seed value (default: 0)
 * @returns A hex string hash
 */
export async function hashFile(file: File | ArrayBuffer, seed = 0): Promise<string> {
    const buffer = file instanceof File ? await file.arrayBuffer() : file;
    const uint8Array = new Uint8Array(buffer);

    let binaryString = '';
    const chunkSize = 8192;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
        binaryString += String.fromCharCode.apply(null, Array.from(chunk));
    }

    const hash = murmur.v3(binaryString, seed);
    return (hash >>> 0).toString(16).padStart(8, '0');
}
